import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import { AddAchData, addAchResolver } from '@/lib/validations/PaymentMethodsSchema';
import { i18n } from '@/utils/i18n';

import BankRoutingNumberInput from '../inputs/BankRoutingNumberInput';
import ChipGroup from '../inputs/ChipGroup';
import TextInputField from '../inputs/TextInputField';
import Text from '../Text';
import FormWrapper from './FormWrapper';

interface AddAchFormProps {
  initialData?: AddAchData;
  onDone?: (data: AddAchData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const AddAchForm = ({ initialData, onDone, doneLabel = i18n.t('mix.done'), isLoading = false }: AddAchFormProps) => {
  const {
    control,
    handleSubmit,
    setFocus,
    setError,
    clearErrors,
    reset,
    formState: { isValid, errors }
  } = useForm<AddAchData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: addAchResolver
  });

  const onSubmit: SubmitHandler<AddAchData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <FormWrapper horizontalPadding={0}>
      <View style={styles.formContainer}>
        <View style={styles.inputsContainer}>
          <ChipGroup
            control={control}
            name="checkingSavingType"
            groupLabel={i18n.t('payment_methods.account_type')}
            options={[
              { label: i18n.t('payment_methods.ach_checking'), value: 'CHECKING' },
              { label: i18n.t('payment_methods.ach_saving'), value: 'SAVINGS' }
            ]}
          />

          <ChipGroup
            control={control}
            name="personalBusinessType"
            groupLabel={i18n.t('payment_methods.account_type')}
            options={[
              { label: i18n.t('payment_methods.ach_personal'), value: 'PERSONAL' },
              { label: i18n.t('payment_methods.ach_business'), value: 'BUSINESS' }
            ]}
          />

          <TextInputField
            name="nameOnAccount"
            label={i18n.t('payment_methods.ach_name_label')}
            placeholder={i18n.t('payment_methods.ach_name_placeholder')}
            control={control}
            returnKeyLabel="next"
            returnKeyType="next"
            onSubmitEditing={() => setFocus('accountNumber')}
          />

          <TextInputField
            name="accountNumber"
            label={i18n.t('payment_methods.ach_account_number_label')}
            placeholder={i18n.t('payment_methods.ach_account_number_placeholder')}
            control={control}
            type="numeric-string"
            returnKeyLabel="done"
            returnKeyType="done"
            onSubmitEditing={() => setFocus('routingNumber')}
          />

          <BankRoutingNumberInput
            control={control}
            name="routingNumber"
            setError={setError}
            clearErrors={clearErrors}
            returnKeyLabel="done"
            returnKeyType="done"
          />
        </View>
        {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

        <BrandedButton loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
          {doneLabel}
        </BrandedButton>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    width: '100%',
    justifyContent: 'space-between'
  },

  inputsContainer: {
    flex: 1,
    width: '100%',
    gap: 15
  },

  title: {
    fontSize: 22,
    color: 'black',
    alignSelf: 'center'
  },

  options: {
    flexDirection: 'row',
    width: '100%',
    gap: 10,
    flex: 1,
    justifyContent: 'center'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  validatingContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 2
  },

  validatingText: {
    color: 'gray',
    fontSize: 13
  },

  bankInfoContainer: {
    padding: 10,
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
    borderRadius: 10,
    backgroundColor: 'lightgray'
  },

  bankInfoText: {
    flex: 1,
    fontSize: 16,
    color: 'black'
  }
});

export default AddAchForm;
